/**
 * prestige.ts — reset the operation for permanent prestige points, and spend
 * them on the skill tree. The square-root formula keeps lifetime totals bounded.
 */
import { newGame, type GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { BALANCE, SKILL_BY_ID } from '../data';

/** Prestige points the player would earn by resetting right now. */
export function prestigeGain(state: GameState): number {
  const { K, scale, exp, minLifetime } = BALANCE.prestige;
  if (state.stats.lifetimeEarnings < minLifetime) return 0;
  return Math.floor(K * Math.pow(state.stats.lifetimeEarnings / scale, exp));
}

export function canPrestige(state: GameState): boolean {
  return prestigeGain(state) > 0;
}

/**
 * Perform a prestige: bank the gained points and reset progress, KEEPING prestige
 * points, totals, skills, achievements, settings, and the RNG stream.
 */
export function doPrestige(state: GameState, now: number, bus?: EventBus): boolean {
  const gain = prestigeGain(state);
  if (gain <= 0) return false;

  const carry = {
    points: state.prestige.points + gain,
    totalEarned: state.prestige.totalEarned + gain,
    resets: state.prestige.resets + 1,
    skills: state.prestige.skills,
  };
  const achievements = state.achievements;
  const settings = state.settings;
  const createdAt = state.meta.createdAt;
  const playtimeMs = state.meta.playtimeMs;
  const rng = state.rng;

  const fresh = newGame(now);
  fresh.prestige = carry;
  fresh.achievements = achievements;
  fresh.settings = settings;
  fresh.meta.createdAt = createdAt;
  fresh.meta.playtimeMs = playtimeMs;
  fresh.rng = rng;

  // Copy the fresh fields back onto the live object (in place) so references hold.
  Object.assign(state, fresh);

  bus?.emit('prestige', { points: gain, total: carry.totalEarned });
  bus?.emit('notify', { kind: 'reward', message: `Prestige für ${gain} Punkte!` });
  return true;
}

// --- Skill tree ---
export function skillCost(state: GameState, id: string): number {
  const s = SKILL_BY_ID[id];
  if (!s) return Infinity;
  const lvl = state.prestige.skills[id] ?? 0;
  return Math.ceil(s.baseCost * Math.pow(s.costGrowth, lvl));
}
export function skillUnlocked(state: GameState, id: string): boolean {
  const s = SKILL_BY_ID[id];
  if (!s) return false;
  if (!s.requires?.skill) return true;
  const [reqId, reqLvl] = s.requires.skill;
  return (state.prestige.skills[reqId] ?? 0) >= reqLvl;
}
export function canBuySkill(state: GameState, id: string): boolean {
  const s = SKILL_BY_ID[id];
  if (!s || !skillUnlocked(state, id)) return false;
  const lvl = state.prestige.skills[id] ?? 0;
  if (lvl >= s.maxLevel) return false;
  return state.prestige.points >= skillCost(state, id);
}
export function buySkill(state: GameState, id: string, bus?: EventBus): boolean {
  if (!canBuySkill(state, id)) return false;
  const cost = skillCost(state, id);
  state.prestige.points -= cost;
  state.prestige.skills[id] = (state.prestige.skills[id] ?? 0) + 1;
  bus?.emit('notify', { kind: 'success', message: `${SKILL_BY_ID[id]!.name} verbessert` });
  return true;
}
